import { BaseModuleI, ModuleI, OptionI } from "../../interfaces";
import { getAllModulesByProfileId, parameterGetNoAuthByCode } from "../../apis";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { QUALITY_PHOTO_IMAGE_CODE } from "../../constants";
import { useModulesBackupStore } from "./router-backup.store";

type DashboardCodes =
  | "DASHBOARD_CLIENT"
  | "DASHBOARD_CLIENT_ADMINISTRATOR"
  | "DASHBOARD_ADMINISTRATOR"
  | "DASHBOARD_PROCESS_ONE"
  | "DASHBOARD_PROCESS_TWO"
  | "DASHBOARD_PROCESS_THREE"
  | null;

type State = {
  options: OptionI[];
  modules: ModuleI[];
  moduleMap: Record<string, BaseModuleI>;
  profileId: string;
  clientId: number | null;
  client: string | null;
  clientIdentification: string | null;
  avatar: string;
  cloneCatalogue: number | null;
  cloneTask: number | null;
  fullName: string;
  firstName: string;
  section: string;
  typeDashboardCode: DashboardCodes;
  isLoading: boolean;
  previousModuleBase: string;
  photoWidth: { ideal: number };
  photoHeight: { ideal: number };
};

type Actions = {
  setPhotoWidth: (photoWidth: State["photoWidth"]) => void;
  setPhotoHeight: (photoHeight: State["photoHeight"]) => void;
  setPreviousModuleBase: (
    previousModuleBase: State["previousModuleBase"]
  ) => void;
  setLoading: (loading: State["isLoading"]) => void;
  setClientId: (clientId: State["clientId"]) => void;
  setClientIdentification: (
    clientIdentification: State["clientIdentification"]
  ) => void;
  setTypeDashboardCode: (typeDashboardCode: DashboardCodes) => void;
  setClient: (client: State["client"]) => void;
  setFirstName: (firstName: State["firstName"]) => void;
  setProfileId: (profileId: string) => void;
  setOptions: (options: OptionI[]) => void;
  setModules: (modules: ModuleI[]) => void;
  setAvatar: (avatar: string) => void;
  setFullName: (fullName: string) => void;
  reset: () => void;
  resetOptions: () => void;
  validateModulesFromApi: () => Promise<any>;
  resetModules: () => void;
  getModulesFromApi: () => void;
  setCloneCatalogue: (catalogue: State["cloneCatalogue"]) => void;
  setCloneTask: (task: State["cloneTask"]) => void;
  setSection: (section: State["section"]) => void;
  getQualityPhoto: () => void;
};

const buildModuleMap = (
  modules: BaseModuleI[],
  map: Record<string, BaseModuleI> = {}
): Record<string, BaseModuleI> => {
  for (const module of modules) {
    if (module.to) {
      map[module.to] = module;
    }
    if (module.children && module.children.length > 0) {
      buildModuleMap(module.children, map);
    }
  }
  return map;
};

const initialState: State = {
  options: [],
  moduleMap: {},
  modules: [],
  profileId: "",
  clientId: null,
  client: null,
  typeDashboardCode: null,
  clientIdentification: null,
  avatar: "",
  cloneCatalogue: null,
  cloneTask: null,
  fullName: "",
  firstName: "",
  section: "",
  isLoading: false,
  previousModuleBase: "",
  photoWidth: { ideal: 1920 },
  photoHeight: { ideal: 1200 },
};

export const useRouterStore = create<State & Actions>()(
  persist(
    (set, get) => ({
      photoWidth: { ideal: 1920 },
      photoHeight: { ideal: 1200 },
      options: [],
      modules: [],
      moduleMap: {},
      profileId: "",
      clientId: null,
      client: null,
      clientIdentification: null,
      avatar: "",
      previousModuleBase: "",
      fullName: "",
      firstName: "",
      typeDashboardCode: null,
      cloneCatalogue: null,
      cloneTask: null,
      section: "",
      isLoading: false,
      setPhotoHeight: (photoHeight) => set(() => ({ photoHeight })),
      setPhotoWidth: (photoWidth) => set(() => ({ photoWidth })),
      setPreviousModuleBase: (previousModuleBase) =>
        set((state) => ({ previousModuleBase })),
      setLoading: (loading) => set({ isLoading: loading }),
      setSection: (section) => set(() => ({ section })),
      reset: () => {
        localStorage.removeItem(process.env.REACT_APP_TOKEN_ROUTER!);
        set(initialState);
      },
      resetOptions: () => set((state) => ({ options: [] })),
      resetModules: () => set((state) => ({ modules: [] })),

      getModulesFromApi: async () => {
        // const token = localStorage.getItem(process.env.REACT_APP_TOKEN!);
        // if (!token || !get().profileId) {
        //   localStorage.removeItem(process.env.REACT_APP_TOKEN_ROUTER!);
        //   get().reset();
        //   return;
        // }
        // const { data: dataModules } = await getAllModulesByProfileId(
        //   get().profileId!
        // );
        // if (dataModules) {
        //   get().setModules(dataModules);
        //   get().setOptions(dataModules.flatMap((module) => module.options || []));
        // }
      },
      validateModulesFromApi: async () => {
        set({ isLoading: true });
        try {
          const tokenData = JSON.parse(
            localStorage.getItem(process.env.REACT_APP_TOKEN!) || "{}"
          );
          const token = tokenData?.token ?? null;
          const profileId = tokenData?.user?.profileId ?? get().profileId;

          if (!token || !profileId) {
            console.error("No se encontró un token o profileId");
            set({ isLoading: false });
            return;
          }

          const { data: dataModules } = await getAllModulesByProfileId(
            profileId
          );

          if (dataModules) {
            const opts = Array.from(
              new Map(
                dataModules
                  .flatMap((mod) => mod.options || [])
                  .map((option) => [option.id, option]) // Usar `id` como clave única
              ).values()
            );
            const moduleMap = buildModuleMap(dataModules);

            set({
              modules: dataModules,
              options: opts,
              moduleMap,
            });
            useModulesBackupStore.getState().setModulesBackup(dataModules);

          } else {
            set({ modules: [], options: [] });
          }
          // return Promise.resolve(dataModules);
          return dataModules;
        } catch (error:any) {
          const { modulesBackup } = useModulesBackupStore.getState();
          if (!navigator.onLine || error.response?.status === 401) {
            if (modulesBackup.length > 0) {
              const opts = Array.from(
                new Map(
                  modulesBackup
                    .flatMap((mod) => mod.options || [])
                    .map((option) => [option.id, option])
                ).values()
              );
              set({
                modules: modulesBackup,
                options: opts,
              });
              return modulesBackup;
            }
            set({ modules: [], options: [] });
            return;
          }
          set({ modules: [], options: [] });
          return;
        } finally {
          set({ isLoading: false }); // Finaliza la carga
        }
      },
      getQualityPhoto: async () => {
        const { data: dataQualityPhoto } = await parameterGetNoAuthByCode(
          QUALITY_PHOTO_IMAGE_CODE
        );

        if (!dataQualityPhoto) return;

        const width_ = dataQualityPhoto.value.split(",")[0];
        const width = width_.split(":")[1];
        const height_ = dataQualityPhoto.value.split(",")[1];
        const height = height_.split(":")[1];

        set({
          photoWidth: { ideal: Number(width) },
          photoHeight: { ideal: Number(height) },
        });
      },
      setOptions: (options) => set({ options }),
      setModules: (modules) => set({ modules }),
      setTypeDashboardCode: (typeDashboardCode) =>
        set((state) => ({ typeDashboardCode })),
      setFullName: (fullName) => set((state) => ({ fullName })),
      setProfileId: (profileId) => set((state) => ({ profileId })),
      setClientId: (clientId) => set((state) => ({ clientId })),
      setClientIdentification: (clientIdentification) =>
        set((state) => ({ clientIdentification })),
      setClient: (client) => set((state) => ({ client })),
      setAvatar: (avatar) => set((state) => ({ avatar })),
      setFirstName: (firstName) => set((state) => ({ firstName })),
      setCloneCatalogue: (cloneCatalogue) =>
        set((state) => ({ cloneCatalogue })),
      setCloneTask: (cloneTask) => set((state) => ({ cloneTask })),
    }),
    { name: process.env.REACT_APP_TOKEN_ROUTER! }
  )
);
